import type { GridColumnVisibilityModel, GridEventLookup } from '@mui/x-data-grid-premium';
import { GridLogicOperator } from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { GridViewsControlsProps } from './GridViewsControls';
import type { WriteOutcome } from './gridViewsStorage';
import { useStoredGridViews } from './gridViewsStorage';
import type { ExternalFilterItemIds, GridViewState } from './gridViewState';
import {
  applyViewFilters,
  applyViewState,
  comparableViewState,
  isSameViewState,
  readViewState,
} from './gridViewState';

// The dimensions a view remembers, so nothing else marks it changed.
const TRACKED_EVENTS = [
  'filterModelChange',
  'sortModelChange',
  'paginationModelChange',
  'columnVisibilityModelChange',
  'columnOrderChange',
  'columnIndexChange',
  'densityChange',
  'rowGroupingModelChange',
  'aggregationModelChange',
  'pinnedColumnsChange',
] as const satisfies readonly (keyof GridEventLookup)[];

const SAVE_FAILED: Record<Exclude<WriteOutcome, 'saved'>, string> = {
  unavailable:
    'Could not save this view — your browser storage may be full or blocked. Nothing was changed.',
  'newer-build':
    'Could not save this view — your saved views were written by a newer version of LiftAI. Reload the page and try again. Nothing was changed.',
};

// `crypto.randomUUID` exists only in a secure context.
const createViewId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `view-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

type UseGridViews = {
  storeKey: string;
  initialVisibility?: GridColumnVisibilityModel;
  apiRef: MutableRefObject<GridApiPremium>;
  /** See `ExternalFilterItemIds`: the page's own filter items, which a view never stores. */
  externalFilterItemIds?: ExternalFilterItemIds;
};

const NO_EXTERNAL_IDS: ExternalFilterItemIds = [];

/** Saved table views: the arrangement a reader named and can come back to. */
export const useGridViews = ({
  storeKey,
  initialVisibility,
  apiRef,
  externalFilterItemIds = NO_EXTERNAL_IDS,
}: UseGridViews) => {
  const [stored, writeStored] = useStoredGridViews(storeKey);
  // Only a saved view survives a visit; unsaved changes are discarded on leaving.
  const [activeViewId, setActiveViewId] = useState<string | null>(() => stored.defaultViewId);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // The table as its own code arranges it — what the system entry restores to.
  const baselineRef = useRef<GridViewState | null>(null);
  // What was last saved on the active view — the only thing "edited" is measured against.
  const activeStateRef = useRef<GridViewState | null>(null);
  const activeViewIdRef = useRef<string | null>(activeViewId);

  // MUI hands back a new api object per mount, so a subscription can outlive its event
  // manager while `apiRef` still looks fine. Tracking the instance re-runs the effects below.
  const [gridApi, setGridApi] = useState<GridApiPremium | null>(null);

  // Read by the effects below without re-running them. Declared first so they are current.
  const storedRef = useRef(stored);
  const externalIdsRef = useRef(externalFilterItemIds);
  useEffect(() => {
    storedRef.current = stored;
    externalIdsRef.current = externalFilterItemIds;
  }, [stored, externalFilterItemIds]);

  // Every render on purpose: the render after a remount is the only chance to notice MUI
  // swapped the api object, and a dep list would skip exactly that one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Typed as always present, but MUI nulls it between mounts.
    const api = apiRef.current as GridApiPremium | null;
    if (!api || typeof api.subscribeEvent !== 'function') return;
    if (typeof api.restoreState !== 'function' || api === gridApi) return;
    setGridApi(api);
  });

  const selectView = useCallback((viewId: string | null, state: GridViewState | null) => {
    activeViewIdRef.current = viewId;
    activeStateRef.current = state;
    setActiveViewId(viewId);
  }, []);

  /**
   * Never read back off the grid afterwards: where the page owns the filter model,
   * `restoreState` hands it over rather than writing it and it lands a render later, so
   * reading here captured the filter being replaced and every view opened marked as edited.
   */
  const isEdited = useCallback(
    (against: GridViewState) =>
      !isSameViewState(
        readViewState(apiRef, externalIdsRef.current),
        comparableViewState(apiRef, baselineRef.current ?? {}, against, externalIdsRef.current),
      ),
    [apiRef],
  );

  const applyAndTrack = useCallback(
    (baseline: GridViewState, state: GridViewState | null, keepSeededFilterItems = false) => {
      applyViewState(apiRef, baseline, state, externalIdsRef.current, keepSeededFilterItems);
      activeStateRef.current = state ?? baseline;
      setHasChanges(false);
    },
    [apiRef],
  );

  // A key that changes mid-mount is a different table's set of views, so the one that opens
  // is that key's default rather than whatever was active under the old one.
  const storeKeyRef = useRef(storeKey);
  useEffect(() => {
    if (storeKeyRef.current === storeKey) return;
    storeKeyRef.current = storeKey;
    activeViewIdRef.current = stored.defaultViewId;
    setActiveViewId(stored.defaultViewId);

    const baseline = baselineRef.current;
    const named = stored.views.find((view) => view.id === stored.defaultViewId);
    if (baseline) applyAndTrack(baseline, named?.state ?? null);
  }, [storeKey, stored, applyAndTrack]);

  // Put the active view back on the grid, again after any remount — otherwise a table that
  // briefly swapped itself for an error or a spinner loses its views for the session.
  useEffect(() => {
    if (!gridApi) return;

    // Once only. A remount rebuilds what the grid owns from `initialState`, but the page
    // still holds the reader's own `filterModel`/`sortModel` — re-reading would enshrine
    // those as the table's built-in arrangement.
    //
    // Filters are dropped from it on purpose: a link that arrives pre-filtered has already
    // seeded the page's model by now, and keeping those items here would make the deep
    // link the table's own arrangement — no marker on arrival, and Clear Filters putting
    // the filter straight back instead of removing it.
    baselineRef.current ??= {
      ...readViewState(apiRef, externalIdsRef.current),
      filter: { filterModel: { items: [], logicOperator: GridLogicOperator.And } },
    };
    const baseline = baselineRef.current;

    const named = storedRef.current.views.find((view) => view.id === activeViewIdRef.current);
    activeViewIdRef.current = named?.id ?? null;
    setActiveViewId(activeViewIdRef.current);

    if (named) {
      applyAndTrack(baseline, named.state, true);
    } else {
      // Judged rather than assumed: a link that arrived pre-filtered seeded the model
      // before the grid mounted, so no event will fire to notice it.
      activeStateRef.current = baseline;
      setHasChanges(isEdited(baseline));
    }
  }, [apiRef, gridApi, applyAndTrack, isEdited]);

  useEffect(() => {
    if (!gridApi) return;

    const onChange = () => {
      const active = activeStateRef.current;
      if (active) setHasChanges(isEdited(active));
    };

    const unsubscribes = TRACKED_EVENTS.map((event) => gridApi.subscribeEvent(event, onChange));
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [gridApi, isEdited]);

  const activate = useCallback(
    (viewId: string | null) => {
      const baseline = baselineRef.current;
      if (!baseline) return;

      const view = stored.views.find((candidate) => candidate.id === viewId) ?? null;
      activeViewIdRef.current = view?.id ?? null;
      setActiveViewId(activeViewIdRef.current);
      applyAndTrack(baseline, view?.state ?? null);
    },
    [stored.views, applyAndTrack],
  );

  // Storage can move under this hook — another tab, or a write that never landed — and the
  // toolbar would go on offering a view that is gone.
  useEffect(() => {
    const activeId = activeViewIdRef.current;
    if (!activeId) return;

    const named = stored.views.find((view) => view.id === activeId);
    if (!named) {
      activate(null);
      return;
    }
    // Overwritten elsewhere: the grid keeps what is on screen, but what counts as saved moves on.
    if (!isSameViewState(named.state, activeStateRef.current ?? named.state)) {
      activeStateRef.current = named.state;
      setHasChanges(isEdited(named.state));
    }
  }, [stored.views, activate, isEdited]);

  const saveView = useCallback(
    (name: string) => {
      const state = readViewState(apiRef, externalFilterItemIds);
      const id = createViewId();
      const outcome = writeStored((prev) => ({
        ...prev,
        views: [...prev.views, { id, name, state, createdAt: Date.now() }],
      }));

      if (outcome !== 'saved') {
        setSaveError(SAVE_FAILED[outcome]);
        return false;
      }
      setSaveError(null);
      selectView(id, state);
      setHasChanges(false);
      return true;
    },
    [apiRef, writeStored, selectView, externalFilterItemIds],
  );

  const overwriteView = useCallback(
    (viewId: string) => {
      const state = readViewState(apiRef, externalFilterItemIds);
      const outcome = writeStored((prev) => ({
        ...prev,
        views: prev.views.map((view) => (view.id === viewId ? { ...view, state } : view)),
      }));

      if (outcome !== 'saved') {
        setSaveError(SAVE_FAILED[outcome]);
        return false;
      }
      setSaveError(null);
      selectView(viewId, state);
      setHasChanges(false);
      return true;
    },
    [apiRef, writeStored, selectView, externalFilterItemIds],
  );

  const renameView = useCallback(
    (viewId: string, name: string) => {
      const outcome = writeStored((prev) => ({
        ...prev,
        views: prev.views.map((view) => (view.id === viewId ? { ...view, name } : view)),
      }));
      setSaveError(outcome === 'saved' ? null : SAVE_FAILED[outcome]);
      return outcome === 'saved';
    },
    [writeStored],
  );

  const deleteView = useCallback(
    (viewId: string) => {
      const outcome = writeStored((prev) => ({
        views: prev.views.filter((view) => view.id !== viewId),
        defaultViewId: prev.defaultViewId === viewId ? null : prev.defaultViewId,
      }));
      setSaveError(outcome === 'saved' ? null : SAVE_FAILED[outcome]);
      if (outcome === 'saved' && viewId === activeViewIdRef.current) activate(null);
      return outcome === 'saved';
    },
    [writeStored, activate],
  );

  const discardChanges = useCallback(() => {
    const baseline = baselineRef.current;
    const active = activeStateRef.current;
    if (!baseline) return;

    applyAndTrack(baseline, activeViewIdRef.current ? active : null);
  }, [applyAndTrack]);

  const setDefaultView = useCallback(
    (viewId: string | null) => {
      const outcome = writeStored((prev) => ({ ...prev, defaultViewId: viewId }));
      setSaveError(outcome === 'saved' ? null : SAVE_FAILED[outcome]);
      return outcome === 'saved';
    },
    [writeStored],
  );

  const dismissError = useCallback(() => setSaveError(null), []);

  // Clear Filters on a table with views: back to the active view's filters, not to none.
  const resetFilters = useCallback(() => {
    const active = activeViewIdRef.current ? activeStateRef.current : baselineRef.current;
    applyViewFilters(apiRef, active, externalIdsRef.current);
  }, [apiRef]);

  // Oldest first, below the system entry the menu always lists ahead of them.
  const orderedViews = useMemo(
    () => [...stored.views].sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0)),
    [stored.views],
  );

  const views: GridViewsControlsProps = useMemo(
    () => ({
      views: orderedViews,
      activeViewId,
      defaultViewId: stored.defaultViewId,
      hasChanges,
      saveError,
      onActivate: activate,
      onSaveView: saveView,
      onDiscardChanges: discardChanges,
      onOverwriteView: overwriteView,
      onRenameView: renameView,
      onDeleteView: deleteView,
      onSetDefaultView: setDefaultView,
      onDismissError: dismissError,
    }),
    [
      orderedViews,
      stored.defaultViewId,
      activeViewId,
      hasChanges,
      saveError,
      activate,
      saveView,
      discardChanges,
      overwriteView,
      renameView,
      deleteView,
      setDefaultView,
      dismissError,
    ],
  );

  // Handed back rather than declared a second time at the call site: the toolbar has to
  // agree with the views layer on which items the page owns, and two lists drifted apart.
  return {
    views,
    resetFilters,
    columnVisibilityModel: initialVisibility,
    globalFilterItemIds: externalFilterItemIds,
  };
};
